<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { storeToRefs } from "pinia";

import type {
  IntegrationStatus,
  IntegrationDriverInfo,
} from "@/types/integrationInstance";

import { integrationsStore } from "@/stores/integrations";
import { addErrorBottom } from "@/stores/messages";

import {
  searchLanguageText,
} from "@/mixins/translatedProperty";

import IntegrationListItem from "@/components/integration/IntegrationListItem.vue";
import AddIntegration from "@/components/integration/AddIntegration.vue";

const store = integrationsStore();
const { statuses, notConfiguredCustomDrivers, notConfiguredExternalDrivers } = storeToRefs(store);

const props = defineProps({
  searchText: {
    type: String,
    default: "",
  },
});

defineExpose({
  loadData
});

const loading = ref(false);
const elAddIntegration = ref();

const filteredInstanceList = computed<IntegrationStatus[]>(() => {
  const search = props.searchText.toLowerCase();
  return statuses.value.filter((item: IntegrationStatus) => {
    return (
      searchLanguageText(item.name, search) ||
      (item.integration_id || "").toLowerCase().includes(search) ||
      item.driver_id?.toLowerCase().includes(search)
    );
  });
});

const filteredDriverList = computed<IntegrationDriverInfo[]>(() => {
  const search = props.searchText.toLowerCase();
  return notConfiguredCustomDrivers.value.concat(notConfiguredExternalDrivers.value).filter((driver: IntegrationDriverInfo) => {
    return (
      searchLanguageText(driver.name, search) ||
      driver.developer_name?.toLowerCase().includes(search) ||
      driver.driver_id?.toLowerCase().includes(search)
    );
  });
});

async function startNotConfigured(integration: IntegrationStatus) {
  if (integration.driver_id) {
    try {
      const driverData = await store.getDriver(integration.driver_id);
      elAddIntegration.value.startNotConfiguredDriverSetup(driverData);
    } catch (e: any) {
      addErrorBottom(e);
    }
  } else {
    elAddIntegration.value.startNotConfiguredDriverSetup(integration);
  }
}

async function deleteIntegration(integration: IntegrationStatus) {
  try {
    await store.deleteIntegration(integration);
    await store.getStatuses(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

async function loadData() {
  try {
    await store.getStatuses(true);
    await store.getNotConfiguredCustomDrivers(true);
    await store.getNotConfiguredExternalDrivers(true);
  } catch (e: any) {
    addErrorBottom(e);
  }
}

onMounted(async () => {
  loading.value = true;
  await loadData();
  loading.value = false;
});

</script>
<template>
  <div class="integration-list">
    <div class="integration-list__body">
      <div class="ent-list">
        <div v-if="(filteredInstanceList && filteredInstanceList.length > 0) || (filteredDriverList && filteredDriverList.length > 0)" class="ent-list__body-wrapper" v-overflow-indicator>
          <div class="ent-list__body">
            <IntegrationListItem
              v-for="inst in filteredInstanceList"
              @start-not-configured="startNotConfigured"
              @delete="deleteIntegration"
              :integration="inst"
            />
            <IntegrationListItem
              v-for="inst in filteredDriverList"
              @start-not-configured="startNotConfigured"
              @delete="deleteIntegration"
              :integration="inst"
            />
          </div>
        </div>
        <p v-if="statuses.length < 1 && notConfiguredCustomDrivers.length < 1 && notConfiguredExternalDrivers.length < 1 && !loading" class="ent-list__description">{{ $t('integration.no_integrations') }}</p>
        <p v-else-if="filteredInstanceList.length < 1 && filteredDriverList.length < 1 && !loading" class="ent-list__description">{{ $t('ui.nothing_was_found') }}</p>
      </div>
    </div>
  </div>
  <AddIntegration
    ref="elAddIntegration"
  />
</template>
